import { kebabCase } from 'lodash';
import React, { useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { EventID, EVENT_ID, EVENT_PREFIX } from '../common/Analytics';
import { SubmissionState } from '../redux/modules/BonusActivity';
import {
  COUPON_REWARD,
  descriptionOfRewardCategory,
  DONATION_REWARD,
  GIFT_REWARD,
  MONEY_REWARD,
  PHYSICAL_REWARD,
  RewardCategory,
  RewardType,
  SUBVENTION_REWARD,
} from '../redux/modules/BonusShop';
import { FilterType } from '../redux/modules/BonusTimeline';
import { Profile } from '../redux/modules/Profile';
import { State } from '../redux/types/State';

export const AnalyticsContext = React.createContext({
  enabled: false,

  trackOnboarding: (_page: number) => {},

  trackLogin: () => {},
  trackLoginForgotPassword: () => {},
  trackLoginLogin: () => {},
  trackLoginHelp: () => {},
  trackLoginRegister: () => {},

  trackBonusTerms: () => {},
  trackBonusTermsAccept: () => {},
  trackBonusTermsDecline: () => {},

  trackBonusDashboard: () => {},
  trackBonusDashboardAddInsured: () => {},
  trackBonusDashboardBonusShop: () => {},
  trackBonusDashboardBonusTimeline: () => {},
  trackBonusDashboardInfo: () => {},

  trackBonusDashboardTimeline: () => {},
  trackBonusDashboardTimelineAddActivity: () => {},
  trackBonusDashboardTimelineItem: (_title: string, _submissionState: SubmissionState) => {},

  trackBonusDashboardShop: () => {},
  trackBonusDashboardShopDonation: () => {},
  trackBonusDashboardShopGift: () => {},
  trackBonusDashboardShopRewards: () => {},
  trackBonusDashboardShopSubvention: () => {},

  trackBonusFilterApply: () => {},
  trackBonusFilterReset: () => {},
  trackBonusFilterSelectType: (_type: FilterType) => {},
  trackBonusFilterSelectYear: (_year: number) => {},

  trackBonusShopRewards: () => {},
  trackBonusShopRewardsItem: (_name: string) => {},
  trackBonusShopRewardsSelectCategory: (_category: RewardCategory) => {},
  trackBonusShopRewardsShowAvailable: (_showAvailable: boolean) => {},

  trackBonusShopRewardDetail: (_name: string) => {},
  trackBonusShopRewardDetailSelectReward: (_name: string, _type: RewardType) => {},
  trackBonusShopRewardDetailSelectRewardAgain: (_name: string, _type: RewardType) => {},
  trackBonusShopRewardDetailOrderReward: (_name: string, _type: RewardType) => {},
  trackBonusShopRewardDetailConfirmOrder: (_name: string, _type: RewardType) => {},

  trackBonusActivities: (_year: number) => {},
  trackBonusActivitiesBooklet: () => {},
  trackBonusActivitiesProminentBooklet: () => {},
  trackBonusActivitiesSelectActivity: (_title: string) => {},
  trackBonusActivitiesSelectStatus: (_status: string) => {},
  trackBonusActivitiesSelectYear: (_year: number) => {},

  trackBonusActivityDetail: (_title: string) => {},
  trackBonusActivityDetailCallSupport: () => {},
  trackBonusActivityDetailLink: () => {},
  trackBonusActivityDetailReuploadPhoto: (_title: string) => {},
  trackBonusActivityDetailSubmit: (_title: string) => {},
  trackBonusActivityDetailUploadPhoto: (_title: string) => {},

  trackSettings: () => {},
  trackSettingsBonusFAQ: () => {},
  trackSettingsEndBonusProgram: () => {},
  trackSettingsFeedback: () => {},
  trackSettingsLicenses: () => {},
  trackSettingsPersonalData: () => {},
  trackSettingsSecurity: () => {},
  trackSettingsTracking: () => {},

  trackFeedbackLikes: () => {},
  trackFeedbackDislikes: () => {},
  trackFeedbackLikesRate: () => {},
  trackFeedbackLikesNotNow: () => {},
  trackFeedbackDislikesServiceCenter: () => {},
  trackFeedbackDislikesPrivacy: () => {},
  trackFeedbackDislikesSend: () => {},

  trackBonusInfoDetailedInfo: () => {},
  trackBonusAddInsured: () => {},
  trackBonusAddInsuredAdd: () => {},
});

const ERROR_MONITORING_ENABLED_BREADCRUMB = 'errorMonitoringEnabled';

export type AnalyticsProviderProps = {
  children: React.ReactNode;
  onTrack: (eventId: string, params?: { [name: string]: string | number | boolean }) => void;
  addBreadcrumb: (breadcrumb: Breadcrumb) => void;
};

export default function AnalyticsProvider({
  children,
  onTrack,
  addBreadcrumb,
}: AnalyticsProviderProps) {
  const profile = useSelector<State, Profile | null | undefined>((state) => state.profile.profile);

  const handleTrack = (
    eventId: EventID,
    args: (string | number | boolean)[] = [],
    params?: { [name: string]: string | number | boolean },
  ) => {
    if (profile?.analyticsEnabled) {
      onTrack(
        [
          EVENT_PREFIX,
          // eslint-disable-next-line @typescript-eslint/prefer-regexp-exec
          eventId.match(/%s/) ? eventId.replace(/%s/, (args.pop() ?? '').toString()) : eventId,
        ]
          .concat(args.map((arg) => kebabCase(arg.toString())))
          .join('/'),
        params,
      );
    }
  };

  useEffect(() => {
    addBreadcrumb({
      category: ERROR_MONITORING_ENABLED_BREADCRUMB,
      data: { errorMonitoringEnabled: profile?.errorMonitoringEnabled },
      timestamp: Date.now(),
    });
  }, [addBreadcrumb, profile?.errorMonitoringEnabled]);

  return (
    <AnalyticsContext.Provider
      value={{
        enabled: !!profile?.analyticsEnabled,

        trackOnboarding: (page) => handleTrack(EVENT_ID.ONBOARDING, [page]),

        trackLogin: () => handleTrack(EVENT_ID.LOGIN),
        trackLoginForgotPassword: () => handleTrack(EVENT_ID.LOGIN_FORGOT_PASSWORD),
        trackLoginHelp: () => handleTrack(EVENT_ID.LOGIN_HELP),
        trackLoginLogin: () => handleTrack(EVENT_ID.LOGIN_LOGIN),
        trackLoginRegister: () => handleTrack(EVENT_ID.LOGIN_REGISTER),

        trackBonusTerms: () => handleTrack(EVENT_ID.BONUS_TERMS),
        trackBonusTermsAccept: () => handleTrack(EVENT_ID.BONUS_TERMS_ACCEPT),
        trackBonusTermsDecline: () => handleTrack(EVENT_ID.BONUS_TERMS_ACCEPT),

        trackBonusDashboard: () => handleTrack(EVENT_ID.BONUS_DASHBOARD),
        trackBonusDashboardAddInsured: () => handleTrack(EVENT_ID.BONUS_DASHBOARD_ADD_INSURED),
        trackBonusDashboardBonusShop: () => handleTrack(EVENT_ID.BONUS_DASHBOARD_BONUS_SHOP),
        trackBonusDashboardBonusTimeline: () =>
          handleTrack(EVENT_ID.BONUS_DASHBOARD_BONUS_TIMELINE),
        trackBonusDashboardInfo: () => handleTrack(EVENT_ID.BONUS_DASHBOARD_INFO),

        trackBonusDashboardTimeline: () => handleTrack(EVENT_ID.BONUS_DASHBOARD_TIMELINE),
        trackBonusDashboardTimelineAddActivity: () =>
          handleTrack(EVENT_ID.BONUS_DASHBOARD_TIMELINE_ADD_ACTIVITY),
        trackBonusDashboardTimelineItem: (title, submissionState) =>
          handleTrack(EVENT_ID.BONUS_DASHBOARD_TIMELINE_ITEM, [title, submissionState]),

        trackBonusDashboardShop: () => handleTrack(EVENT_ID.BONUS_DASHBOARD_SHOP),
        trackBonusDashboardShopDonation: () => handleTrack(EVENT_ID.BONUS_DASHBOARD_SHOP_DONATION),
        trackBonusDashboardShopGift: () => handleTrack(EVENT_ID.BONUS_DASHBOARD_SHOP_GIFT),
        trackBonusDashboardShopRewards: () => handleTrack(EVENT_ID.BONUS_DASHBOARD_SHOP_REWARDS),
        trackBonusDashboardShopSubvention: () =>
          handleTrack(EVENT_ID.BONUS_DASHBOARD_SHOP_SUBVENTION),

        trackBonusFilterApply: () => handleTrack(EVENT_ID.BONUS_FILTER_APPLY),
        trackBonusFilterReset: () => handleTrack(EVENT_ID.BONUS_FILTER_RESET),
        trackBonusFilterSelectType: (type) =>
          handleTrack(EVENT_ID.BONUS_FILTER_SELECT_TYPE, [type]),
        trackBonusFilterSelectYear: (year) =>
          handleTrack(EVENT_ID.BONUS_FILTER_SELECT_YEAR, [year]),

        trackBonusShopRewards: () => handleTrack(EVENT_ID.BONUS_SHOP_REWARDS),
        trackBonusShopRewardsItem: (name) => handleTrack(EVENT_ID.BONUS_SHOP_REWARDS_ITEM, [name]),
        trackBonusShopRewardsSelectCategory: (category) =>
          handleTrack(EVENT_ID.BONUS_SHOP_REWARDS_SELECT_CATEGORY, [
            descriptionOfRewardCategory(category),
          ]),
        trackBonusShopRewardsShowAvailable: (showAvailable) =>
          handleTrack(EVENT_ID.BONUS_SHOP_REWARDS_SHOW_AVAILABLE, [showAvailable]),

        trackBonusShopRewardDetail: (name) =>
          handleTrack(EVENT_ID.BONUS_SHOP_REWARD_DETAIL, [name]),
        trackBonusShopRewardDetailSelectReward: (name, type) =>
          handleTrack(EVENT_ID.BONUS_SHOP_REWARD_DETAIL_SELECT_REWARD, [name, type], {
            page_target_group: 'Bonus App/Bonusprogramm Prämie',
            page_target_name: 'Bonusprogramm Prämie – Schritt 1 – Prämie auswählen',
            ...((type === MONEY_REWARD || type === SUBVENTION_REWARD || type === GIFT_REWARD) && {
              page_target_value: 0.2,
            }),
            ...(type === DONATION_REWARD && {
              page_target_value: 0.3,
            }),
            ...((type === COUPON_REWARD || type === PHYSICAL_REWARD) && {
              page_target_value: 0.5,
            }),
          }),
        trackBonusShopRewardDetailSelectRewardAgain: (name, type) =>
          handleTrack(EVENT_ID.BONUS_SHOP_REWARD_DETAIL_SELECT_REWARD, [name, type], {
            page_target_group: 'Bonus App/Bonusprogramm Prämie',
            page_target_name: 'Bonusprogramm Prämie – Schritt 2 – Prämie auswählen',
            ...((type === MONEY_REWARD || type === SUBVENTION_REWARD || type === GIFT_REWARD) && {
              page_target_value: 0.5,
            }),
            ...(type === DONATION_REWARD && {
              page_target_value: 0.6,
            }),
          }),
        trackBonusShopRewardDetailOrderReward: (name, type) =>
          handleTrack(EVENT_ID.BONUS_SHOP_REWARD_DETAIL_ORDER_REWARD, [name, type], {
            page_target_group: 'Bonus App/Bonusprogramm Prämie',
            page_target_name: 'Bonusprogramm Prämie – Schritt 3 – Prämie bestellen',
            page_target_value: 0.7,
          }),
        trackBonusShopRewardDetailConfirmOrder: (name, type) =>
          handleTrack(EVENT_ID.BONUS_SHOP_REWARD_DETAIL_CONFIRM_ORDER, [name, type], {
            page_target_group: 'Bonus App/Bonusprogramm Prämie',
            ...((type === MONEY_REWARD || type === SUBVENTION_REWARD || type === GIFT_REWARD) && {
              page_target_name: 'Bonusprogramm Prämie – Schritt 4 - Verbindlich bestellen',
            }),
            ...(type === DONATION_REWARD && {
              page_target_name: 'Bonusprogramm Prämie – Schritt 3 - Verbindlich bestellen',
            }),
            ...((type === COUPON_REWARD || type === PHYSICAL_REWARD) && {
              page_target_name: 'Bonusprogramm Prämie – Schritt 2 - Verbindlich bestellen',
            }),
            page_target_value: 1,
          }),

        trackBonusActivities: (year) => handleTrack(EVENT_ID.BONUS_ACTIVITIES, [year]),
        trackBonusActivitiesBooklet: () => {},
        trackBonusActivitiesProminentBooklet: () => {},
        trackBonusActivitiesSelectActivity: (title) =>
          handleTrack(EVENT_ID.BONUS_ACTIVITIES_SELECT_ACTIVITY, [title], {
            page_target_group: 'Bonus App/Bonusmaßnahme',
            page_target_name: 'Bonusmaßnahme – Schritt 1 – Maßnahmenauswahl',
            page_target_value: 0.3,
          }),
        trackBonusActivitiesSelectStatus: (status) =>
          handleTrack(EVENT_ID.BONUS_ACTIVITIES_SELECT_STATUS, [status]),
        trackBonusActivitiesSelectYear: (year) =>
          handleTrack(EVENT_ID.BONUS_ACTIVITIES_SELECT_YEAR, [year]),

        trackBonusActivityDetail: (title) => handleTrack(EVENT_ID.BONUS_ACTIVITY_DETAIL, [title]),
        trackBonusActivityDetailCallSupport: () =>
          handleTrack(EVENT_ID.BONUS_ACTIVITY_DETAIL_CALL_SUPPORT),
        trackBonusActivityDetailLink: () => handleTrack(EVENT_ID.BONUS_ACTIVITY_DETAIL_LINK),
        trackBonusActivityDetailReuploadPhoto: (title) =>
          handleTrack(EVENT_ID.BONUS_ACTIVITY_DETAIL_REUPLOAD_PHOTO, [title], {
            page_target_group: 'Bonus App/Bonusmaßnahme',
            page_target_name: 'Bonusmaßnahme – Schritt 2 – Nachweis hochladen',
            page_target_value: 0.6,
          }),
        trackBonusActivityDetailSubmit: (title) =>
          handleTrack(EVENT_ID.BONUS_ACTIVITY_DETAIL_SUBMIT, [title], {
            page_target_group: 'Bonus App/Bonusmaßnahme',
            page_target_name: 'Bonusmaßnahme – Schritt 3 – Nachweis einreichen',
            page_target_value: 1,
          }),
        trackBonusActivityDetailUploadPhoto: (title) =>
          handleTrack(EVENT_ID.BONUS_ACTIVITY_DETAIL_UPLOAD_PHOTO, [title], {
            page_target_group: 'Bonus App/Bonusmaßnahme',
            page_target_name: 'Bonusmaßnahme – Schritt 2 – Nachweis hochladen',
            page_target_value: 0.6,
          }),

        trackSettings: () => handleTrack(EVENT_ID.SETTINGS),
        trackSettingsBonusFAQ: () => handleTrack(EVENT_ID.SETTINGS_BONUS_FAQ),
        trackSettingsEndBonusProgram: () => handleTrack(EVENT_ID.SETTINGS_END_BONUS_PROGRAM),
        trackSettingsFeedback: () => handleTrack(EVENT_ID.SETTINGS_FEEDBACK),
        trackSettingsLicenses: () => handleTrack(EVENT_ID.SETTINGS_LICENSES),
        trackSettingsPersonalData: () => handleTrack(EVENT_ID.SETTINGS_PERSONAL_DATA),
        trackSettingsSecurity: () => handleTrack(EVENT_ID.SETTINGS_SECURITY),
        trackSettingsTracking: () => handleTrack(EVENT_ID.SETTINGS_TRACKING),

        trackFeedbackLikes: () => handleTrack(EVENT_ID.FEEDBACK_LIKES),
        trackFeedbackDislikes: () => handleTrack(EVENT_ID.FEEDBACK_DISLIKES),
        trackFeedbackLikesRate: () => handleTrack(EVENT_ID.FEEDBACK_LIKES_RATE),
        trackFeedbackLikesNotNow: () => handleTrack(EVENT_ID.FEEDBACK_LIKES_NOT_NOW),
        trackFeedbackDislikesServiceCenter: () =>
          handleTrack(EVENT_ID.FEEDBACK_DISLIKES_SERVICE_CENTER),
        trackFeedbackDislikesPrivacy: () => handleTrack(EVENT_ID.FEEDBACK_DISLIKES_PRIVACY),
        trackFeedbackDislikesSend: () => handleTrack(EVENT_ID.FEEDBACK_DISLIKES_SEND),

        trackBonusInfoDetailedInfo: () => handleTrack(EVENT_ID.BONUS_INFO_DETAILED_INFO),
        trackBonusAddInsured: () => handleTrack(EVENT_ID.BONUS_ADD_INSURED),
        trackBonusAddInsuredAdd: () => handleTrack(EVENT_ID.BONUS_ADD_INSURED_ADD),
      }}
    >
      {children}
    </AnalyticsContext.Provider>
  );
}

export function useAnalytics() {
  return useContext(AnalyticsContext);
}

type Breadcrumb = {
  category?: string;
  timestamp?: number;
  data?: any;
};

export function isErrorMonitoringEnabled(breadcrumbs: Breadcrumb[] = []) {
  const filteredBreadcrumbs = breadcrumbs
    .filter((breadcrumb) => breadcrumb?.category === ERROR_MONITORING_ENABLED_BREADCRUMB)
    .sort((a, b) => {
      const timeA = a?.timestamp || 0;
      const timeB = b?.timestamp || 0;

      return timeA - timeB;
    });

  return (
    filteredBreadcrumbs.length > 0 &&
    filteredBreadcrumbs[filteredBreadcrumbs.length - 1].data?.errorMonitoringEnabled
  );
}
